// Daten der Dienstleisterliste: je Tabelle eine (seitenweise) Abfrage, parallel – keine Abfrage je Dienstleister.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { alleSeiten } from '../supabase/alleSeiten';
import type {
  BewertungRoh, DlKategorieRoh, DlListenDaten, DlObjektRoh, DlRoh, KategorieRoh, NachweisZeileRoh, ObjektRoh, RechnungRoh, TicketRoh,
} from './liste';

export interface DlListeDaten extends DlListenDaten {
  /** vollständige Zeilen (select *) – auch für Export und Import-Abgleich */
  dienstleister: (DlRoh & Record<string, unknown>)[];
}

export type DlListeZustand = { status: 'laedt' } | { status: 'fehler'; meldung: string } | { status: 'ok'; daten: DlListeDaten };

/** Gemeldet nach Änderungen (Import, Neuanlage, Kategorien), damit die Liste neu lädt. */
export const DL_GEAENDERT = 'immo-dienstleister-geaendert';
export function meldeDienstleisterGeaendert(id: string | null = null): void {
  window.dispatchEvent(new CustomEvent(DL_GEAENDERT, { detail: id }));
}

export async function ladeDlListe(): Promise<DlListeDaten> {
  const [dienstleister, kategorien, dlKategorien, dlObjekte, objekte, nachweise, rechnungen, tickets, bewertungen] = await Promise.all([
    alleSeiten<DlListeDaten['dienstleister'][number]>((von, bis) => supabase.from('dienstleister').select('*').order('name').order('id').range(von, bis)),
    alleSeiten<KategorieRoh>((von, bis) => supabase.from('dienstleister_kategorien').select('id, name, ist_handwerk, aktiv, sortierung').order('sortierung').order('id').range(von, bis)),
    alleSeiten<DlKategorieRoh>((von, bis) => supabase.from('dienstleister_kategorie').select('dienstleister_id, kategorie_id').order('dienstleister_id').order('kategorie_id').range(von, bis)),
    alleSeiten<DlObjektRoh>((von, bis) => supabase.from('dienstleister_objekt')
      .select('dienstleister_id, objekt_id, von, bis, vertragsart, aktiv, kuendigung_zum').order('dienstleister_id').order('objekt_id').range(von, bis)),
    alleSeiten<ObjektRoh>((von, bis) => supabase.from('objekte').select('*').order('objektnummer').order('id').range(von, bis)),
    alleSeiten<NachweisZeileRoh>((von, bis) => supabase.from('dienstleister_nachweise').select('dienstleister_id, art, gueltig_bis').order('id').range(von, bis)),
    // Eingangsrechnungen mit zugeordnetem Dienstleister (Ausgangsrechnungen zählen nicht)
    alleSeiten<RechnungRoh>((von, bis) => supabase.from('rechnungen').select('dienstleister_id, brutto, status, datum')
      .not('dienstleister_id', 'is', null).or('richtung.is.null,richtung.eq.eingang').order('id').range(von, bis)),
    alleSeiten<TicketRoh>((von, bis) => supabase.from('tickets').select('dienstleister_id, status')
      .not('dienstleister_id', 'is', null).is('geloescht_am', null).order('id').range(von, bis)),
    alleSeiten<BewertungRoh>((von, bis) => supabase.from('dienstleister_bewertungen').select('dienstleister_id, sterne').order('id').range(von, bis)),
  ]);
  return { dienstleister, kategorien, dlKategorien, dlObjekte, objekte, nachweise, rechnungen, tickets, bewertungen };
}

export function useDienstleisterListe(): { zustand: DlListeZustand; neuLaden: () => void } {
  const [zustand, setZustand] = useState<DlListeZustand>({ status: 'laedt' });
  const [runde, setRunde] = useState(0);
  useEffect(() => {
    let aktiv = true;
    ladeDlListe()
      .then(daten => { if (aktiv) setZustand({ status: 'ok', daten }); })
      .catch((e: unknown) => { if (aktiv) setZustand({ status: 'fehler', meldung: e instanceof Error ? e.message : 'Unbekannter Fehler' }); });
    return () => { aktiv = false; };
  }, [runde]);
  useEffect(() => {
    const hoeren = () => setRunde(r => r + 1);
    window.addEventListener(DL_GEAENDERT, hoeren);
    return () => window.removeEventListener(DL_GEAENDERT, hoeren);
  }, []);
  const neuLaden = useCallback(() => setRunde(r => r + 1), []);
  return { zustand, neuLaden };
}
